"use client";

import {
  Key,
  SelectBase,
  SelectBaseListBox,
  SelectBasePopover,
  SelectBaseTriggerButton,
  SelectBaseValue,
  SelectItem,
  Text,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useTranslations } from "@zapier/i18n";
import { useRouterParams } from "@zapier/shared";

type ViewOption = "connections" | "apps";

const VIEW_OPTIONS: ViewOption[] = ["connections", "apps"];

function isViewOption(value: string): value is ViewOption {
  return (VIEW_OPTIONS as string[]).includes(value);
}

// Every query param that could be set on either view today, minus `view`
// itself, so a view switch lands on a clean URL with only `?view=` (when not
// the default). `details` and `managedAuthViolation` are intentionally omitted
// here — `useRouterParams.setParams` always strips both regardless of the
// passed omit list.
const PARAMS_TO_CLEAR_ON_VIEW_SWITCH = [
  "app",
  "apps",
  "appsOperator",
  "connection",
  "connections",
  "connectionsOperator",
  "folderType",
  "lastRun",
  "memberId",
  "order",
  "owner",
  "owners",
  "ownersOperator",
  "ownerStatus",
  "page",
  "search",
  "shared",
  "status",
  "virtual",
];

export function ConnectionsViewSelect() {
  const t = useTranslations("connections-view-select");
  const { view, setParams } = useRouterParams();

  const selectedView: ViewOption =
    view && isViewOption(view) ? view : "connections";

  const handleChange = (key: Key | null) => {
    if (typeof key !== "string" || !isViewOption(key)) return;
    if (key === selectedView) return;
    // Per APCON-53: clear every query param on view switch (matches today's
    // behavior — switching routes used to lose all params). Done in a single
    // setParams call so we don't read stale React state between two pushes.
    setParams({ view: key }, PARAMS_TO_CLEAR_ON_VIEW_SWITCH);
  };

  return (
    <SelectBase
      aria-label={t("label")}
      selectedKey={selectedView}
      onChange={handleChange}
    >
      <SelectBaseTriggerButton size="medium">
        <SelectBaseValue size="medium">
          {({ selectedText, isPlaceholder }) =>
            isPlaceholder || !selectedText ? (
              t("placeholder")
            ) : (
              <>
                <Text tag="span" type="Body1" color="TextWeakest">
                  {t("label")}:
                </Text>{" "}
                {selectedText}
                <Icon name="arrowDropdown" color="TextWeakest" />
              </>
            )
          }
        </SelectBaseValue>
      </SelectBaseTriggerButton>
      <SelectBasePopover size="medium">
        <SelectBaseListBox size="medium">
          <SelectItem value="connections" textValue={t("options.connections")}>
            {t("options.connections")}
          </SelectItem>
          <SelectItem value="apps" textValue={t("options.apps")}>
            {t("options.apps")}
          </SelectItem>
        </SelectBaseListBox>
      </SelectBasePopover>
    </SelectBase>
  );
}
